import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { EmojiEvents, Redeem, VolunteerActivism } from "@mui/icons-material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import { PRIZES, PRIZE_SEASON, PrizeLeaderboard, prizeWinners } from "./prizes";
import styles from "./Fantasy2.module.css";

interface PrizePanelProps {
    season: number;
    /** Omit on pages that haven't loaded a leaderboard — the prizes then show without holders. */
    leaderboard?: PrizeLeaderboard;
    /** The division on screen; its own first and second place are the ones named. */
    division?: string | null;
}

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">>;

const PRIZE_ICONS: Record<string, MuiIcon> = {
    prizeFirst: Redeem,
    prizeSecond: VolunteerActivism,
};

const TONE_CLASS: Record<string, string> = {
    gold: styles.rankGold,
    silver: styles.rankSilver,
};

/**
 * What the season's fantasy winners get, plus who is holding each prize position right now.
 *
 * Every division pays out its own prizes, so the holders shown are the ones for `division`.
 * The hub, the create page and the guide all render this from the same {@link PRIZES} list.
 * Renders nothing for a season that had no prizes.
 */
const PrizePanel = ({ season, leaderboard, division }: PrizePanelProps) => {
    const { t } = useTranslation(['pages', 'common']);

    const winners = useMemo(
        () => (leaderboard ? prizeWinners(leaderboard, division ?? null) : []),
        [leaderboard, division],
    );

    if (season !== PRIZE_SEASON) return null;

    return (
        <div className={styles.prizeCard}>
            <div className={styles.prizeHead}>
                <span className={styles.prizeTitle}>
                    <EmojiEvents sx={{ fontSize: 19, color: '#f1c40f' }} />
                    {t('pages:fantasy.prizesTitle')}
                </span>
                <span className={styles.prizeScope}>{t('pages:fantasy.prizeEachDivision')}</span>
            </div>

            {PRIZES.map((prize) => {
                const Icon = PRIZE_ICONS[prize.key] ?? Redeem;
                const holder = winners.find((w) => w.placement === prize.placement);
                return (
                    <div className={styles.prizeRow} key={prize.placement}>
                        <span className={`${styles.rankBadge} ${TONE_CLASS[prize.tone] ?? styles.rankDefault}`}>
                            {prize.placement}
                        </span>
                        <Icon className={styles.prizeIcon} sx={{ fontSize: 18 }} />
                        <span className={styles.prizeText}>{t(`pages:fantasy.${prize.key}`)}</span>
                        {holder && (
                            <Link
                                className={styles.prizeHolder}
                                to={`/fantasy/view/${holder.steamId}/${season}/${holder.division}`}
                                title={t('pages:fantasy.prizeLeaderNow')}
                            >
                                <span className={styles.prizeHolderLabel}>{t('pages:fantasy.prizeLeaderNow')}</span>
                                <strong>{holder.playerName}</strong>
                                <span className={styles.prizeHolderMeta}>
                                    {t('pages:fantasy.pointsCount', { count: holder.points })}
                                </span>
                            </Link>
                        )}
                    </div>
                );
            })}

            <p className={styles.prizeFootnote}>{t('pages:fantasy.prizesDesc')}</p>
        </div>
    );
};

export default PrizePanel;
